import type { UserRole } from "../../types/domain";

/**
 * must-19 — beleid rond verplichte tweefactorauthenticatie voor beheerders.
 *
 * Founder-besluit 29-07-2026: verplicht voor `super_admin` en `tenant_admin`,
 * met 30 dagen overgangsperiode voor accounts die de rol al hadden; daarna
 * fail-closed. Nieuwe beheerders hebben geen overgangsperiode.
 *
 * Pure functie zodat exact dezelfde beslissing in de UI, in de router-gate en
 * (via de edge-mirror) serverside gebruikt kan worden. Twee interpretaties van
 * "MFA verplicht" naast elkaar is precies hoe een gate stilletjes lek raakt.
 */

export const ADMIN_MFA_ROLES: readonly UserRole[] = ["super_admin", "tenant_admin"];

export type AdminMfaInput = {
  role: UserRole | null;
  /** Heeft de gebruiker minstens één GEVERIFIEERDE factor? */
  hasVerifiedFactor: boolean;
  /** Is de huidige sessie op aal2 (tweede factor doorlopen)? */
  sessionAtAal2: boolean;
  /**
   * Is de overgangsperiode verstreken? Komt van de SERVER
   * (`collectr_admin_mfa_requirement`), niet uit een vergelijking met de
   * browserklok: die is manipuleerbaar — wie zijn systeemklok terugzet zou
   * anders uitstel kopen van een verplichting die niet uitstelbaar hoort te
   * zijn. Ontbrekende serverinformatie telt als verstreken (fail-closed).
   */
  graceExpired: boolean;
  /** Resterende tijd volgens de server; alleen voor de waarschuwingstekst. */
  secondsLeft: number;
};

export type AdminMfaOutcome =
  /** Geen beheerder, of alles in orde: doorlaten. */
  | { decision: "allow" }
  /** Beheerder zonder factor, binnen de overgangsperiode: doorlaten mét waarschuwing. */
  | { decision: "warn_enroll"; daysLeft: number }
  /** Beheerder zonder factor, overgangsperiode verstreken: blokkeren tot instellen. */
  | { decision: "require_enroll" }
  /** Factor bestaat maar deze sessie is nog aal1: challenge afdwingen. */
  | { decision: "require_challenge" };

export function isAdminMfaRole(role: UserRole | null): boolean {
  return role != null && ADMIN_MFA_ROLES.includes(role);
}

export function evaluateAdminMfa(input: AdminMfaInput): AdminMfaOutcome {
  if (!isAdminMfaRole(input.role)) return { decision: "allow" };

  /*
   * Volgorde is belangrijk: iemand MET een factor moet altijd eerst de
   * challenge doorlopen, ook binnen de overgangsperiode. Anders zou een
   * beheerder die MFA al instelde alsnog met alleen een wachtwoord binnen
   * kunnen — dan is de factor decoratie.
   */
  if (input.hasVerifiedFactor) {
    return input.sessionAtAal2 ? { decision: "allow" } : { decision: "require_challenge" };
  }

  if (input.graceExpired) return { decision: "require_enroll" };

  const daysLeft = Math.max(0, Math.ceil(input.secondsLeft / (24 * 60 * 60)));
  return { decision: "warn_enroll", daysLeft };
}

/** Leest het aal-niveau uit een Supabase-JWT-payload zonder de sessie te vertrouwen op naam. */
export function sessionIsAal2(accessTokenPayload: { aal?: unknown } | null): boolean {
  return accessTokenPayload?.aal === "aal2";
}

/** Decodeert de payload van een JWT; geeft null bij elke twijfel. */
export function decodeJwtPayload(token: string | null | undefined): Record<string, unknown> | null {
  if (typeof token !== "string") return null;
  const parts = token.split(".");
  if (parts.length !== 3) return null;
  try {
    const base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64 + "=".repeat((4 - (base64.length % 4)) % 4);
    const json =
      typeof atob === "function"
        ? atob(padded)
        : Buffer.from(padded, "base64").toString("binary");
    const parsed: unknown = JSON.parse(json);
    return parsed !== null && typeof parsed === "object" ? (parsed as Record<string, unknown>) : null;
  } catch {
    return null;
  }
}
